import { scientific } from "../lib/format";

/**
 * A slider that moves through a value range in LOG space — right for quantities
 * spanning orders of magnitude (galaxies, stars, planets). Position 0..1 maps
 * to min..max logarithmically; the live value reads out in scientific form.
 */
export default function LogSlider({
  label,
  value,
  min,
  max,
  onChange,
  suffix,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  suffix?: string;
}) {
  const logMin = Math.log10(min);
  const logMax = Math.log10(max);
  const clamped = Math.min(max, Math.max(min, value));
  const pos = (Math.log10(clamped) - logMin) / (logMax - logMin);

  const handle = (t: number) => {
    const v = Math.pow(10, logMin + t * (logMax - logMin));
    onChange(v);
  };

  return (
    <div className="select-none">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-xs uppercase tracking-wider text-white/70">
          {label}
        </span>
        <span className="font-mono text-sm text-cyan">
          {scientific(value)}
          {suffix ? (
            <span className="ml-1 text-xs text-white/55">{suffix}</span>
          ) : null}
        </span>
      </div>
      <div className="relative">
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-gradient-to-r from-cyan-deep to-cyan"
          style={{ width: `${pos * 100}%` }}
        />
        <input
          type="range"
          min={0}
          max={1}
          step={0.002}
          value={pos}
          onChange={(e) => handle(parseFloat(e.target.value))}
          aria-label={label}
          className="factor-range relative z-10 w-full"
        />
      </div>
    </div>
  );
}
